import { DEFAULT_SETTINGS, SETTINGS_STORAGE_KEY, type Settings, SettingsSchema } from '@md/shared';

/**
 * Préférences du joueur : son et vibration (concept §5.2, §9).
 *
 * Même prudence que la progression : le stockage est une donnée non fiable,
 * on la valide par zod (schéma dans `@md/shared`) et on retombe silencieusement
 * sur les valeurs par défaut si elle est corrompue ou si `localStorage` est
 * indisponible.
 */
export type { Settings };
export { DEFAULT_SETTINGS };

export interface SettingsStore {
  get(): Settings;
  /** Applique un changement partiel et renvoie l'état complet. */
  set(patch: Partial<Settings>): Settings;
  subscribe(listener: (settings: Settings) => void): () => void;
}

export interface SettingsStoreOptions {
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'> | undefined;
}

function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function createSettingsStore(options: SettingsStoreOptions = {}): SettingsStore {
  const storage = options.storage === undefined ? safeStorage() : options.storage;
  const listeners = new Set<(settings: Settings) => void>();

  const load = (): Settings => {
    const raw = storage?.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_SETTINGS };
    }
    try {
      const parsed = SettingsSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : { ...DEFAULT_SETTINGS };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  };

  let current = load();

  const persist = (): void => {
    try {
      storage?.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(current));
    } catch {
      // Quota dépassé ou stockage refusé : la préférence reste en mémoire.
    }
  };

  return {
    get: () => ({ ...current }),
    set(patch: Partial<Settings>): Settings {
      current = { ...current, ...patch };
      persist();
      for (const listener of listeners) {
        listener({ ...current });
      }
      return { ...current };
    },
    subscribe(listener: (settings: Settings) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
